import { ActionIconButton } from '@dealstack/frontend/shared/components/ActionIconButton/ActionIconButton.tsx';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number';
import { CurrencyType, LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Flex, Text } from '@mantine/core';
import { isNumber } from 'lodash';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { HoldingOrLoan, HoldingTableColumn } from './holdingTable.types';
import { isLoanHolding } from './holdingTable.utils';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	currency?: CurrencyType;
	formattedValuationDate: string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const getHoldingTableColumns = ({
	tMipManage,
	currency,
	formattedValuationDate,
	getSecurityTypeTranslation,
}: Props): ExtendedColumnDef<HoldingOrLoan>[] => {
	return [
		createTextColumn<HoldingOrLoan>({
			id: HoldingTableColumn.type,
			headerText: tMipManage('investors.details.holdings_table.security_type'),
			columnLabel: tMipManage('investors.details.holdings_table.security_type'),
			enableSorting: true,
			accessorFn: (row) => getSecurityTypeTranslation(row.type),
		}),
		createTextColumn<HoldingOrLoan>({
			id: HoldingTableColumn.name,
			accessorKey: 'name',
			headerText: tMipManage('investors.details.holdings_table.security_name'),
			columnLabel: tMipManage('investors.details.holdings_table.security_name'),
			enableSorting: true,
		}),
		createNumberColumn<HoldingOrLoan>({
			id: HoldingTableColumn.volume,
			accessorKey: 'volume',
			headerText: tMipManage('investors.details.holdings_table.total_volume'),
			columnLabel: tMipManage('investors.details.holdings_table.total_volume'),
			enableSorting: true,
			cell: ({ row }) => {
				const holding = row.original;
				return (
					<Flex justify="flex-end" w="100%">
						<Text>
							{isNumber(holding.volume) ? (
								<AmountFormatted
									value={holding.volume}
									currency={currency}
									showCurrency={false}
									decimalScale={extractDecimalPrecisionFromUnitDenominator(
										('securityUnitDenominator' in holding
											? holding.securityUnitDenominator
											: undefined) ?? 1
									)}
								/>
							) : (
								holding.volume
							)}
						</Text>
					</Flex>
				);
			},
		}),
		createNumberColumn<HoldingOrLoan>({
			id: HoldingTableColumn.value,
			headerText: tMipManage('investors.details.holdings_table.total_value', {
				valuationDate: formattedValuationDate,
			}),
			columnLabel: tMipManage('investors.details.holdings_table.total_value', {
				valuationDate: formattedValuationDate,
			}),
			enableSorting: true,
			accessorFn: (row) => {
				const v = row.value ?? 0;
				return isLoanHolding(row) ? -v : v;
			},
			sortingFn: 'basic',
			cell: ({ row }) => {
				const holding = row.original;
				return (
					<Flex justify="flex-end" w="100%">
						<Text>
							<AmountFormatted
								value={holding.value}
								currency={currency}
								withMinusSymbol={isLoanHolding(holding)}
							/>
						</Text>
					</Flex>
				);
			},
		}),
		createNumberColumn<HoldingOrLoan>({
			id: HoldingTableColumn.cost,
			headerText: tMipManage('investors.details.holdings_table.total_cost'),
			columnLabel: tMipManage('investors.details.holdings_table.total_cost'),
			enableSorting: true,
			accessorFn: (row) => {
				const c = row.cost ?? 0;
				return isLoanHolding(row) ? -c : c;
			},
			sortingFn: 'basic',
			cell: ({ row }) => {
				const holding = row.original;
				return (
					<Flex justify="flex-end" w="100%">
						<Text>
							<AmountFormatted
								value={holding.cost}
								currency={currency}
								withMinusSymbol={isLoanHolding(holding)}
							/>
						</Text>
					</Flex>
				);
			},
		}),
		{
			id: HoldingTableColumn.vesting,
			header: () => (
				<Flex justify="center" w="100%">
					<Text fw={600}>{tMipManage('investors.details.holdings_table.vesting')}</Text>
				</Flex>
			),
			columnLabel: tMipManage('investors.details.holdings_table.vesting'),
			enableSorting: false,
			enableColumnFilter: false,
			enableHiding: false,
			minSize: 20,
			maxSize: 20,
			cell: ({ row }) => {
				const holding = row.original;
				const isSubjectToVesting =
					'isSubjectToVesting' in holding && !!holding.isSubjectToVesting;
				const securityId = 'securityId' in holding ? holding.securityId : undefined;

				return (
					<Flex justify="center" w="100%">
						{isSubjectToVesting ? (
							<ActionIconButton
								iconVariant={row.getIsExpanded() ? 'unview-v3' : 'eye'}
								onClick={() => row.toggleExpanded()}
								dataTestId={makeComplexDataTestId(
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TABLE_VESTING_ICON,
									securityId
								)}
							/>
						) : (
							<Text>n/a</Text>
						)}
					</Flex>
				);
			},
		},
	];
};
